// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Check, UserMinus, Share2, AlertCircle, Loader2 } from 'lucide-react';
import {
  listSessionShares,
  listTenantMembers,
  revokeSessionShare,
  shareSession,
} from '../../../utils/sessionsApi';

/**
 * Share a workbench engagement with teammates in the same tenant.
 *
 * - Shows a searchable list of every other user in the tenant.
 * - Lets the analyst pick recipients with checkboxes; "Share" creates the rows.
 * - Lists current shares with revoke (×) buttons.
 * - Server enforces tenant scope; this component is purely UI.
 */
const ShareModal = ({ sessionId, sessionName, onClose, onChange }) => {
  const [members, setMembers] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // user_id -> true
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listTenantMembers(), listSessionShares(sessionId)])
      .then(([membersRes, sharesRes]) => {
        if (cancelled) return;
        setMembers(membersRes.members || []);
        setShares(sharesRes.shares || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Already-shared user_ids — gray these out in the picker.
  const alreadySharedIds = useMemo(
    () => new Set(shares.map((s) => s.user_id)),
    [shares]
  );

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) =>
      q ? (m.email || '').toLowerCase().includes(q) : true
    );
  }, [members, search]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected]
  );

  const toggleSelect = (id) =>
    setSelected((s) => ({ ...s, [id]: !s[id] }));

  const handleShare = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await shareSession(sessionId, { user_ids: selectedIds });
      // Refresh the existing-shares list and clear the selection.
      const fresh = await listSessionShares(sessionId);
      setShares(fresh.shares || []);
      setSelected({});
      setSavedToast(
        res.new_shares > 0
          ? `Shared with ${res.new_shares} ${res.new_shares === 1 ? 'person' : 'people'}`
          : 'Already shared'
      );
      setTimeout(() => setSavedToast(null), 1800);
      if (onChange) onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (userId) => {
    setSaving(true);
    setError(null);
    try {
      await revokeSessionShare(sessionId, userId);
      const fresh = await listSessionShares(sessionId);
      setShares(fresh.shares || []);
      if (onChange) onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="qoe-wb-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="qoe-wb-modal"
        role="dialog"
        aria-label="Share engagement"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="qoe-wb-modal-header">
          <div>
            <div className="qoe-wb-eyebrow">Share engagement</div>
            <h2>{sessionName || 'Untitled engagement'}</h2>
            <p className="qoe-wb-modal-subtitle">
              Share this QoE workbench with teammates in your tenant. Recipients
              will see it on their workbench landing page with a "Shared by you"
              badge and can review or edit adjustments alongside you.
            </p>
          </div>
          <button className="qoe-wb-btn ghost" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="qoe-wb-modal-body">
          {error && (
            <div className="qoe-wb-row-error fail" role="alert" style={{ maxWidth: 'unset' }}>
              <AlertCircle size={12} />
              <div>{error}</div>
            </div>
          )}

          {loading ? (
            <div className="qoe-wb-loading">
              <div className="spinner"></div>
              <p>Loading teammates…</p>
            </div>
          ) : (
            <>
              {/* Existing shares ----- */}
              <section className="qoe-wb-modal-section">
                <h3>Currently shared with ({shares.length})</h3>
                {shares.length === 0 ? (
                  <p className="qoe-wb-modal-meta">
                    Nobody yet — pick teammates below to share with.
                  </p>
                ) : (
                  <ul className="qoe-wb-share-list">
                    {shares.map((s) => (
                      <li key={s.user_id}>
                        <span className="qoe-wb-share-email">{s.user_email}</span>
                        <button
                          className="qoe-wb-share-revoke"
                          onClick={() => handleRevoke(s.user_id)}
                          disabled={saving}
                          title="Remove share"
                        >
                          <UserMinus size={12} /> Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Picker ----- */}
              <section className="qoe-wb-modal-section">
                <h3>Add teammates</h3>
                {members.length === 0 ? (
                  <p className="qoe-wb-modal-meta">
                    No other users in this tenant yet. Invite teammates from
                    Account &rarr; Tenant settings to start collaborating.
                  </p>
                ) : (
                  <>
                    <div className="qoe-wb-landing-search">
                      <Search size={14} />
                      <input
                        type="text"
                        placeholder="Search by email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <ul className="qoe-wb-share-picker">
                      {filteredMembers.map((m) => {
                        const already = alreadySharedIds.has(m.id);
                        const checked = !!selected[m.id];
                        return (
                          <li
                            key={m.id}
                            className={`qoe-wb-share-row ${already ? 'disabled' : ''}`}
                            onClick={() => !already && toggleSelect(m.id)}
                          >
                            <span className="qoe-wb-share-checkbox">
                              {already ? <Check size={12} /> : checked && <Check size={12} />}
                            </span>
                            <span className="qoe-wb-share-email">{m.email}</span>
                            {already && (
                              <span className="qoe-wb-share-already">already shared</span>
                            )}
                          </li>
                        );
                      })}
                      {filteredMembers.length === 0 && (
                        <li className="qoe-wb-modal-meta" style={{ padding: '8px 0' }}>
                          No matches.
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </section>
            </>
          )}
        </div>

        <footer className="qoe-wb-modal-footer">
          {savedToast && (
            <span className="qoe-wb-modal-toast">
              <Check size={12} /> {savedToast}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button className="qoe-wb-btn ghost qoe-wb-tactile" onClick={onClose}>
            Close
          </button>
          <button
            className="qoe-wb-btn qoe-wb-tactile"
            onClick={handleShare}
            disabled={saving || selectedIds.length === 0}
          >
            {saving ? <Loader2 size={12} className="spinning" /> : <Share2 size={12} />}
            {saving
              ? 'Sharing…'
              : selectedIds.length === 0
              ? 'Pick someone'
              : `Share with ${selectedIds.length}`}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ShareModal;
